import React, { useMemo } from 'react';
import { Grid } from '@material-ui/core';
import { debounce } from 'lodash';
import { useDispatchFilter, useSelectFilter } from 'hooks/planning/useFilter';
import { IFilterOptions } from 'interfaces/SelectOptions';
import FilterBarItem from 'components/molecules/FilterBarItem';
import { useInfiniteQuery, useQuery } from 'react-query';
import { getGroupOptions } from 'services/msgraph-group';
import { postUserNameFilter } from 'services/msgraph-users';
import qs from 'qs';
import { IUserNameFilterData } from 'interfaces/Grid';
import useStyles from './style';

const TAKE = 10;

const GroupPermissionTopContainer = () => {
  const classes = useStyles();
  const changeFilter = useDispatchFilter();

  const [filterUserGroupOptions, setFilterUserGroupOptions] = React.useState<
    IFilterOptions[]
  >([]);

  const [userGroupFilterOptionsContext, userNameFilterOptionsContext] = [
    useSelectFilter('userGroup'),
    useSelectFilter('userName'),
  ];

  const allFilterContexts = [
    userGroupFilterOptionsContext,
    userNameFilterOptionsContext,
  ];

  const [filterUserNameOptions, setFilterUserNameOptions] = React.useState<
    IFilterOptions[]
  >([]);

  const pauseFilterUserName = useMemo<boolean>(() => {
    return userNameFilterOptionsContext.length > 0;
  }, [userNameFilterOptionsContext]);

  const groupIds = React.useMemo(
    () => userGroupFilterOptionsContext.map(item => Number(item?.value)),
    [userGroupFilterOptionsContext],
  );

  const [inputFieldValues, setInputFieldValues] = React.useState<{
    [key: string]: string;
  }>({});

  const userName = React.useMemo(() => {
    if (inputFieldValues?.userName && inputFieldValues?.userName.length >= 1) {
      return inputFieldValues?.userName;
    }
    return userNameFilterOptionsContext.map(item => String(item?.label));
  }, [inputFieldValues?.userName, userNameFilterOptionsContext]);

  const queryfilterUserGroup = useQuery({
    queryKey: ['userGroupFilterOptionsContext'],
    queryFn: async () => {
      const { data } = await getGroupOptions();

      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item?.group,
          value: item?.id,
        };
      });
    },
    onSuccess: (data: IFilterOptions[]) => {
      setFilterUserGroupOptions(data);
    },
    enabled: true,
  });

  const getNextPageParam = React.useCallback(
    (lastPage: IUserNameFilterData, allPages: IUserNameFilterData[]) => {
      const allResults = allPages.map(page => page.result).flat().length;
      if (allResults === 0) return 1;

      const hasNextPage = allResults < lastPage.count;
      if (hasNextPage) return allResults / TAKE + 1;

      return undefined;
    },
    [],
  );

  const queryFilterUserName = useInfiniteQuery({
    queryKey: [
      'userNameFilterOptionsContext',
      inputFieldValues?.userName,
      ...allFilterContexts,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const qsparams = {
        userName,
        page: pageParam,
        take: TAKE,
      } as { page: number; take: number };

      const params = {
        groupIds,
      } as { [key: string]: (string | number)[] | string | number };

      const { data } = await postUserNameFilter(qs.stringify(qsparams), params);
      return data;
    },
    getNextPageParam,
    onSuccess: data => {
      const options = data?.pages
        .map(item => item.result)
        .flat()
        .map(item => ({
          label: item.user,
          value: item.id,
        }));
      setFilterUserNameOptions(options);
    },
    enabled: !pauseFilterUserName,
    cacheTime: 0,
  });

  const handleChange = ({
    name,
    value,
  }: {
    name: string;
    value: IFilterOptions[];
  }) => {
    changeFilter(name, value);
  };

  const debouncesetInputFieldValues = React.useMemo(
    () =>
      debounce((name, value) => {
        setInputFieldValues(currentValues => ({
          ...currentValues,
          [name]: value,
        }));
      }, 600),
    [],
  );

  return (
    <div className={classes.root}>
      <Grid container spacing={1}>
        <Grid item xs={8}>
          <Grid container spacing={1}>
            <Grid item xs={3}>
              <FilterBarItem
                id="userGroup"
                label="Grupo do Usuário"
                name="userGroup"
                options={filterUserGroupOptions}
                loading={
                  queryfilterUserGroup.isLoading ||
                  queryfilterUserGroup?.isFetching
                }
                onFilterChange={handleChange}
                initialState={userGroupFilterOptionsContext}
              />
            </Grid>

            <Grid item xs={3} className={classes.inputList}>
              <FilterBarItem
                id="userName"
                label="Nome do Usuário"
                name="userName"
                options={filterUserNameOptions}
                onFilterChange={handleChange}
                hasNextPage={queryFilterUserName.hasNextPage}
                fetchNextPage={queryFilterUserName.fetchNextPage}
                setInputFieldValue={name => {
                  debouncesetInputFieldValues('userName', name);
                }}
                loading={
                  queryFilterUserName.isLoading ||
                  queryFilterUserName?.isFetching
                }
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default GroupPermissionTopContainer;
